import { APIError } from '../utils/errors.js';

export class BaseAPIService {
	constructor(config = {}) {
		this.workspace = config.workspace;
		this.sessionToken = null;
		this.sessionExpiry = null;
		this.metadata = config.metadata || null;
		this.identitySyncedToken = null;
		this.mock = config.mock || false;
		this.env = config.env || 'production';
		this.baseURL = this._getBaseURL(config);

		this._loadStoredSession();
		this._loadStoredMetadata();
		if (this.isSessionValid() && this.metadata) {
			this.identitySyncedToken = this.sessionToken;
		}
	}

	_getBaseURL(config) {
		if (config.apiUrl) return config.apiUrl;

		const ENV_URLS = {
			production: {
				base: 'https://api.product7.io/api/v1',
				withWorkspace: (ws) => `https://${ws}.api.product7.io/api/v1`,
			},
			staging: {
				base: 'https://staging.api.product7.io/api/v1',
				withWorkspace: (ws) => `https://${ws}.staging.api.product7.io/api/v1`,
			},
			localstack: {
				base: 'http://localhost:1323/api/v1',
				withWorkspace: (ws) => `http://${ws}.localhost:1323/api/v1`,
			},
		};

		let env = this.env;
		if (!env || env === 'production') {
			const hostname =
				(typeof window !== 'undefined' && window.location?.hostname) || '';
			const port =
				(typeof window !== 'undefined' && window.location?.port) || '';
			if (hostname.includes('staging')) {
				env = 'staging';
			} else if (hostname === 'app.localhost' && port === '3005') {
				env = 'localstack';
			}
		}

		const envConfig = ENV_URLS[env] || ENV_URLS.production;
		return this.workspace
			? envConfig.withWorkspace(this.workspace)
			: envConfig.base;
	}

	async init(metadata = undefined) {
		if (metadata !== undefined) {
			this.setMetadata(metadata);
		}

		if (this.isSessionValid()) {
			return { sessionToken: this.sessionToken };
		}

		if (!this.workspace) {
			throw new APIError(400, 'Missing workspace for initialization');
		}

		if (this.mock) {
			return this._initMockSession();
		}

		return this._initRealSession();
	}

	async _initMockSession() {
		this.sessionToken = 'mock_session_' + Date.now();
		this.sessionExpiry = new Date(Date.now() + 3600 * 1000);
		this.identitySyncedToken = null;
		this._storeSession();
		return {
			sessionToken: this.sessionToken,
			config: {
				primaryColor: '#21244A',
				backgroundColor: '#ffffff',
				textColor: '#1F2937',
				boardName: 'feature-requests',
				size: 'medium',
				displayMode: 'modal',
			},
			expiresIn: 3600,
		};
	}

	async _initRealSession() {
		const payload = {
			workspace: this.workspace,
		};

		try {
			const response = await this._makeRequest('/widget/init', {
				method: 'POST',
				body: JSON.stringify(payload),
				headers: { 'Content-Type': 'application/json' },
			});
			const initData = this._extractInitResponseData(response);

			this.sessionToken = initData.sessionToken;
			this.sessionExpiry = new Date(Date.now() + initData.expiresIn * 1000);
			this.identitySyncedToken = null;
			this._storeSession();

			return {
				sessionToken: this.sessionToken,
				config: initData.config,
				widgets: initData.widgets,
				expiresIn: initData.expiresIn,
				status: initData.status,
				message: initData.message,
				configVersion: initData.configVersion,
			};
		} catch (error) {
			throw new APIError(
				error.status || 500,
				`Failed to initialize widget: ${error.message}`,
				error.response
			);
		}
	}

	_extractInitResponseData(response) {
		const payload =
			response && typeof response.data === 'object'
				? response.data
				: response || {};

		const configData =
			payload.config && typeof payload.config === 'object'
				? payload.config
				: {};

		const sessionToken =
			payload.session_token ||
			payload.sessionToken ||
			configData.session_token ||
			configData.sessionToken;

		const expiresIn = Number(
			payload.expires_in ??
				payload.expiresIn ??
				configData.expires_in ??
				configData.expiresIn
		);

		if (!sessionToken) {
			throw new APIError(500, 'Invalid init response: missing session_token');
		}

		if (!Number.isFinite(expiresIn) || expiresIn <= 0) {
			throw new APIError(500, 'Invalid init response: missing expires_in');
		}

		return {
			sessionToken,
			expiresIn,
			config: configData,
			widgets:
				payload.widgets && typeof payload.widgets === 'object'
					? payload.widgets
					: null,
			configVersion:
				configData.config_version ??
				configData.configVersion ??
				payload.config_version ??
				payload.configVersion ??
				null,
			status: response?.status ?? payload?.status ?? true,
			message: response?.message ?? payload?.message ?? null,
		};
	}

	async _ensureSession() {
		if (!this.isSessionValid()) {
			await this.init();
		}
		if (!this.sessionToken) {
			throw new APIError(401, 'No valid session token available');
		}
	}

	async _handleAuthRetry(method, ...args) {
		try {
			return await method.apply(this, args);
		} catch (error) {
			if (error.status === 401) {
				this.sessionToken = null;
				this.sessionExpiry = null;
				await this.init();
				await this._restoreIdentity();
				return await method.apply(this, args);
			}
			throw error;
		}
	}

	async identify(metadata = this.metadata) {
		if (metadata !== undefined) {
			this.setMetadata(metadata);
		}

		if (!this.metadata) {
			throw new APIError(400, 'Missing user context for identify');
		}

		await this._ensureSession();

		const payload = this._buildIdentifyPayload(this.metadata);

		if (this.mock) {
			this.identitySyncedToken = this.sessionToken;
			return {
				status: true,
				identified: true,
				data: {
					user_id: payload.user_id || null,
					email: payload.email || null,
					name: payload.name || null,
				},
			};
		}

		const response = await this._makeRequest('/widget/identify', {
			method: 'POST',
			body: JSON.stringify(payload),
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${this.sessionToken}`,
			},
		});

		this.identitySyncedToken = this.sessionToken;
		return response;
	}

	isSessionValid() {
		return (
			this.sessionToken && this.sessionExpiry && new Date() < this.sessionExpiry
		);
	}

	setMetadata(metadata) {
		this.metadata = metadata || null;
		this.identitySyncedToken = null;
		if (this.metadata) {
			this._storeData('product7_metadata', this.metadata);
		} else {
			this._removeData('product7_metadata');
		}
	}

	getMetadata() {
		return this.metadata;
	}

	clearSession() {
		this.sessionToken = null;
		this.sessionExpiry = null;
		this.identitySyncedToken = null;
		this.contactId = null;
		this.contactEmail = null;
		this.contactName = null;
		this._removeData('product7_session');
		this._removeData('product7_metadata');
		this._removeData('product7_contact');
	}

	_storeSession() {
		if (typeof localStorage === 'undefined') return;
		try {
			const sessionData = {
				token: this.sessionToken,
				expiry: this.sessionExpiry.toISOString(),
				workspace: this.workspace,
			};
			this._storeData('product7_session', sessionData);
		} catch (error) {
			// Silent fail
		}
	}

	_loadStoredSession() {
		if (typeof localStorage === 'undefined') return false;
		try {
			const stored = localStorage.getItem('product7_session');
			if (!stored) return false;

			const sessionData = JSON.parse(stored);

			// Invalidate mock tokens when not in mock mode (and vice versa)
			const isMockToken =
				sessionData.token && sessionData.token.startsWith('mock_');
			if (isMockToken !== this.mock) {
				localStorage.removeItem('product7_session');
				return false;
			}

			if (
				this.workspace &&
				sessionData.workspace &&
				sessionData.workspace !== this.workspace
			) {
				localStorage.removeItem('product7_session');
				return false;
			}

			this.sessionToken = sessionData.token;
			this.sessionExpiry = new Date(sessionData.expiry);

			return this.isSessionValid();
		} catch (error) {
			return false;
		}
	}

	_loadStoredMetadata() {
		if (this.metadata || typeof localStorage === 'undefined') return false;

		try {
			const session = localStorage.getItem('product7_session');
			if (session) {
				const sessionData = JSON.parse(session);
				if (
					this.workspace &&
					sessionData.workspace &&
					sessionData.workspace !== this.workspace
				) {
					return false;
				}
			}

			const stored = localStorage.getItem('product7_metadata');
			if (!stored) return false;

			this.metadata = JSON.parse(stored);
			return true;
		} catch (error) {
			return false;
		}
	}

	_storeData(key, value) {
		if (typeof localStorage !== 'undefined') {
			localStorage.setItem(key, JSON.stringify(value));
		}
	}

	_removeData(key) {
		if (typeof localStorage !== 'undefined') {
			localStorage.removeItem(key);
		}
	}

	async _makeRequest(endpoint, options = {}) {
		const url = `${this.baseURL}${endpoint}`;

		try {
			const response = await fetch(url, options);

			if (!response.ok) {
				let errorMessage = `HTTP ${response.status}`;
				let responseData = null;

				try {
					responseData = await response.json();
					errorMessage =
						responseData.message || responseData.error || errorMessage;
				} catch (e) {
					errorMessage = (await response.text()) || errorMessage;
				}

				throw new APIError(response.status, errorMessage, responseData);
			}

			const contentType = response.headers.get('content-type');
			if (contentType && contentType.includes('application/json')) {
				return await response.json();
			}

			return await response.text();
		} catch (error) {
			if (error instanceof APIError) throw error;
			throw new APIError(0, error.message, null);
		}
	}

	_buildQueryParams(params) {
		const queryParams = new URLSearchParams();
		Object.entries(params).forEach(([key, value]) => {
			if (value !== undefined && value !== null) {
				queryParams.append(
					key,
					typeof value === 'object' ? JSON.stringify(value) : value
				);
			}
		});
		return queryParams.toString();
	}

	_getEndpointWithParams(endpoint, params) {
		const queryString = this._buildQueryParams(params);
		return `${endpoint}${queryString ? '?' + queryString : ''}`;
	}

	_buildIdentifyPayload(metadata = {}) {
		const payload = {
			user_id: metadata.user_id,
			email: metadata.email,
			name: metadata.name,
			avatar:
				metadata.profile_picture || metadata.avatar_url || metadata.avatar,
			attributes: metadata.custom_fields || {},
		};

		if (metadata.company) {
			payload.company = metadata.company;
		}

		return payload;
	}

	async _restoreIdentity() {
		if (
			!this.metadata ||
			!this.sessionToken ||
			!this.identitySyncedToken ||
			this.identitySyncedToken === this.sessionToken
		) {
			return;
		}

		await this.identify(this.metadata);
	}
}
